(function initFinchDiamondPreviewCore(root, factory) {
  'use strict';

  var api = factory();

  if (root) root.FinchDiamondPreviewCore = api;
  if (typeof module === 'object' && module.exports) module.exports = api;
})(
  typeof globalThis !== 'undefined'
    ? globalThis
    : typeof window !== 'undefined'
      ? window
      : typeof self !== 'undefined'
        ? self
        : null,
  function createFinchDiamondPreviewCore() {
    'use strict';

    var VERSION = '1.2.0';
    var DEFAULT_DRILL_PITCH_MM = Object.freeze({ square: 2.5, round: 2.8 });
    var COLOR_TARGET_POLICY = Object.freeze([
      Object.freeze({ maxEdgeCm: Infinity, maxColors: 50 }),
    ]);

    var FALLBACK_PALETTE_SOURCE = [
      ['FP001', 'Ink black', '#101114'],
      ['FP002', 'Soft black', '#26262a'],
      ['FP003', 'Graphite', '#414247'],
      ['FP004', 'Slate grey', '#5c6068'],
      ['FP005', 'Mid grey', '#7a7e84'],
      ['FP006', 'Silver grey', '#9da1a5'],
      ['FP007', 'Mist grey', '#c1c4c5'],
      ['FP008', 'Cloud', '#dedfdd'],
      ['FP009', 'Soft white', '#f2f0e8'],
      ['FP010', 'Canvas white', '#fffdf5'],
      ['FP011', 'Ivory', '#f1e2c3'],
      ['FP012', 'Sand', '#d8bf91'],
      ['FP013', 'Camel', '#b8905f'],
      ['FP014', 'Caramel', '#996638'],
      ['FP015', 'Chestnut', '#744425'],
      ['FP016', 'Dark walnut', '#4a2e20'],
      ['FP017', 'Espresso', '#2e201b'],
      ['FP018', 'Blush', '#f2c7bf'],
      ['FP019', 'Dusty rose', '#d99391'],
      ['FP020', 'Coral', '#ef765f'],
      ['FP021', 'Scarlet', '#d93435'],
      ['FP022', 'Crimson', '#a8192e'],
      ['FP023', 'Wine', '#711f35'],
      ['FP024', 'Deep burgundy', '#451d2b'],
      ['FP025', 'Petal pink', '#f7b8d1'],
      ['FP026', 'Rose pink', '#e66aa5'],
      ['FP027', 'Magenta', '#bd2c80'],
      ['FP028', 'Plum', '#76235f'],
      ['FP029', 'Peach', '#f5b27c'],
      ['FP030', 'Tangerine', '#ef7e2f'],
      ['FP031', 'Burnt orange', '#c55421'],
      ['FP032', 'Terracotta', '#98472e'],
      ['FP033', 'Lemon', '#f5dd55'],
      ['FP034', 'Sunflower', '#eebc25'],
      ['FP035', 'Golden ochre', '#c98b20'],
      ['FP036', 'Mustard', '#9f731f'],
      ['FP037', 'Pale lime', '#d8dc83'],
      ['FP038', 'Leaf green', '#92b951'],
      ['FP039', 'Fresh green', '#52a54f'],
      ['FP040', 'Emerald', '#23835d'],
      ['FP041', 'Forest', '#225c43'],
      ['FP042', 'Deep forest', '#173d32'],
      ['FP043', 'Olive', '#7d8038'],
      ['FP044', 'Moss', '#596329'],
      ['FP045', 'Mint', '#a8ddc8'],
      ['FP046', 'Seafoam', '#63bea6'],
      ['FP047', 'Turquoise', '#2aa9aa'],
      ['FP048', 'Deep teal', '#176d75'],
      ['FP049', 'Ice blue', '#c6e3e8'],
      ['FP050', 'Sky blue', '#78c5df'],
      ['FP051', 'Cerulean', '#3195cc'],
      ['FP052', 'Royal blue', '#2d62ad'],
      ['FP053', 'Navy', '#243d75'],
      ['FP054', 'Midnight blue', '#17284e'],
      ['FP055', 'Periwinkle', '#9daee0'],
      ['FP056', 'Cornflower', '#6f82c7'],
      ['FP057', 'Lilac', '#c7addb'],
      ['FP058', 'Violet', '#8d63b6'],
      ['FP059', 'Purple', '#65408e'],
      ['FP060', 'Deep violet', '#3f2b62'],
      ['FP061', 'Warm beige', '#d7b9a5'],
      ['FP062', 'Skin light', '#edc2a2'],
      ['FP063', 'Skin medium', '#c88967'],
      ['FP064', 'Skin deep', '#87553e'],
      ['FP065', 'Aqua', '#57cbd1'],
      ['FP066', 'Cobalt', '#244d96'],
      ['FP067', 'Apple', '#79c843'],
      ['FP068', 'Pine', '#174d37'],
      ['FP069', 'Amber', '#db8f24'],
      ['FP070', 'Brick', '#a83c32'],
      ['FP071', 'Mauve', '#a16884'],
      ['FP072', 'Blue grey', '#718394'],
    ];

    function clamp(value, min, max) {
      return Math.min(max, Math.max(min, value));
    }

    function clampByte(value) {
      return Math.round(clamp(Number(value) || 0, 0, 255));
    }

    function parseHex(hex) {
      var normalized = String(hex || '').trim().replace(/^#/, '');
      if (/^[0-9a-f]{3}$/i.test(normalized)) {
        normalized = normalized.replace(/(.)/g, '$1$1');
      }
      if (!/^[0-9a-f]{6}$/i.test(normalized)) return null;
      return {
        r: parseInt(normalized.slice(0, 2), 16),
        g: parseInt(normalized.slice(2, 4), 16),
        b: parseInt(normalized.slice(4, 6), 16),
      };
    }

    function rgbToHex(rgb) {
      return (
        '#' +
        [rgb.r, rgb.g, rgb.b]
          .map(function toHex(value) {
            return clampByte(value).toString(16).padStart(2, '0');
          })
          .join('')
      );
    }

    function srgbByteToLinear(byte) {
      var value = clamp(Number(byte) || 0, 0, 255) / 255;
      return value <= 0.04045 ? value / 12.92 : Math.pow((value + 0.055) / 1.055, 2.4);
    }

    function linearToSrgbByte(linear) {
      var value = clamp(Number(linear) || 0, 0, 1);
      var encoded =
        value <= 0.0031308 ? value * 12.92 : 1.055 * Math.pow(value, 1 / 2.4) - 0.055;
      return clampByte(encoded * 255);
    }

    function rgbToOklab(rgb) {
      var r = srgbByteToLinear(rgb.r);
      var g = srgbByteToLinear(rgb.g);
      var b = srgbByteToLinear(rgb.b);

      var l = 0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b;
      var m = 0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b;
      var s = 0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b;
      var lRoot = Math.cbrt(l);
      var mRoot = Math.cbrt(m);
      var sRoot = Math.cbrt(s);

      return {
        L: 0.2104542553 * lRoot + 0.793617785 * mRoot - 0.0040720468 * sRoot,
        a: 1.9779984951 * lRoot - 2.428592205 * mRoot + 0.4505937099 * sRoot,
        b: 0.0259040371 * lRoot + 0.7827717662 * mRoot - 0.808675766 * sRoot,
      };
    }

    function oklabToRgb(lab) {
      var lightness = Number(lab && lab.L) || 0;
      var a = Number(lab && lab.a) || 0;
      var b = Number(lab && lab.b) || 0;
      var lRoot = lightness + 0.3963377774 * a + 0.2158037573 * b;
      var mRoot = lightness - 0.1055613458 * a - 0.0638541728 * b;
      var sRoot = lightness - 0.0894841775 * a - 1.291485548 * b;
      var l = lRoot * lRoot * lRoot;
      var m = mRoot * mRoot * mRoot;
      var s = sRoot * sRoot * sRoot;

      return {
        r: linearToSrgbByte(4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s),
        g: linearToSrgbByte(-1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s),
        b: linearToSrgbByte(-0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s),
      };
    }

    function rgbaToOklabBuffer(rgba) {
      var pixelCount = Math.floor((rgba && rgba.length ? rgba.length : 0) / 4);
      var output = new Float64Array(pixelCount * 3);
      for (var pixel = 0; pixel < pixelCount; pixel += 1) {
        var rgbaOffset = pixel * 4;
        var labOffset = pixel * 3;
        var lab = rgbToOklab({
          r: rgba[rgbaOffset],
          g: rgba[rgbaOffset + 1],
          b: rgba[rgbaOffset + 2],
        });
        output[labOffset] = lab.L;
        output[labOffset + 1] = lab.a;
        output[labOffset + 2] = lab.b;
      }
      return output;
    }

    function bilateralFilterOklab(rgba, width, height, options) {
      assertRgbaBuffer(rgba, width, height);
      var settings = options || {};
      var sigmaColor = Math.max(0.0001, Number(settings.sigmaColor) || 0.035);
      var denominatorScale = 2 * sigmaColor * sigmaColor;
      var spatial = [0.42, 0.66, 0.42, 0.66, 1, 0.66, 0.42, 0.66, 0.42];
      var source = rgbaToOklabBuffer(rgba);
      var output = new Float64Array(source.length);

      for (var row = 0; row < height; row += 1) {
        for (var column = 0; column < width; column += 1) {
          var pixel = row * width + column;
          var centerOffset = pixel * 3;
          var centerL = source[centerOffset];
          var centerA = source[centerOffset + 1];
          var centerB = source[centerOffset + 2];
          var totalWeight = 0;
          var totalL = 0;
          var totalA = 0;
          var totalB = 0;

          for (var offsetY = -1; offsetY <= 1; offsetY += 1) {
            var neighborRow = clamp(row + offsetY, 0, height - 1);
            for (var offsetX = -1; offsetX <= 1; offsetX += 1) {
              var neighborColumn = clamp(column + offsetX, 0, width - 1);
              var neighborOffset = (neighborRow * width + neighborColumn) * 3;
              var deltaL = source[neighborOffset] - centerL;
              var deltaA = source[neighborOffset + 1] - centerA;
              var deltaB = source[neighborOffset + 2] - centerB;
              var colorDistance = deltaL * deltaL + deltaA * deltaA + deltaB * deltaB;
              var spatialIndex = (offsetY + 1) * 3 + offsetX + 1;
              var weight = spatial[spatialIndex] * Math.exp(-colorDistance / denominatorScale);
              totalWeight += weight;
              totalL += source[neighborOffset] * weight;
              totalA += source[neighborOffset + 1] * weight;
              totalB += source[neighborOffset + 2] * weight;
            }
          }

          var safeWeight = Math.max(totalWeight, 1e-12);
          output[centerOffset] = totalL / safeWeight;
          output[centerOffset + 1] = totalA / safeWeight;
          output[centerOffset + 2] = totalB / safeWeight;
        }
      }

      return output;
    }

    function oklabDistanceSquared(first, second) {
      var deltaL = first.L - second.L;
      var deltaA = first.a - second.a;
      var deltaB = first.b - second.b;
      return deltaL * deltaL + deltaA * deltaA + deltaB * deltaB;
    }

    function normalizeStoneShape(value) {
      var normalized = String(value == null ? '' : value)
        .trim()
        .toLowerCase();
      if (['square', 'vierkant', 'quadratisch', 'quadratic'].indexOf(normalized) !== -1) {
        return 'square';
      }
      if (['round', 'rond', 'rund', 'circular', 'circle'].indexOf(normalized) !== -1) {
        return 'round';
      }
      return 'square';
    }

    function parseCanvasSize(input) {
      var widthCm;
      var heightCm;

      if (Array.isArray(input) && input.length >= 2) {
        widthCm = Number(input[0]);
        heightCm = Number(input[1]);
      } else if (input && typeof input === 'object') {
        widthCm = Number(input.widthCm != null ? input.widthCm : input.width);
        heightCm = Number(input.heightCm != null ? input.heightCm : input.height);
      } else {
        var match = String(input == null ? '' : input)
          .trim()
          .match(/^(\d+(?:[.,]\d+)?)\s*(?:x|×)\s*(\d+(?:[.,]\d+)?)\s*(?:cm)?$/i);
        if (!match) return null;
        widthCm = Number(match[1].replace(',', '.'));
        heightCm = Number(match[2].replace(',', '.'));
      }

      if (!Number.isFinite(widthCm) || !Number.isFinite(heightCm) || widthCm <= 0 || heightCm <= 0) {
        return null;
      }

      var orientation =
        Math.abs(widthCm - heightCm) < 0.000001
          ? 'square'
          : widthCm > heightCm
            ? 'landscape'
            : 'portrait';

      return Object.freeze({
        widthCm: widthCm,
        heightCm: heightCm,
        widthMm: widthCm * 10,
        heightMm: heightCm * 10,
        aspectRatio: widthCm / heightCm,
        orientation: orientation,
        label: formatMeasurement(widthCm) + 'x' + formatMeasurement(heightCm) + 'cm',
      });
    }

    function formatMeasurement(value) {
      return Number.isInteger(value) ? String(value) : String(value).replace('.', ',');
    }

    function computePhysicalGrid(sizeInput, stoneShape, options) {
      var settings = options || {};
      var size = orientCanvasSize(sizeInput, settings.orientation);
      if (!size) throw new TypeError('Ongeldige canvasmaat. Gebruik bijvoorbeeld "20x30cm".');

      var shape = normalizeStoneShape(stoneShape);
      var pitch = Number(
        settings.pitchMm != null
          ? settings.pitchMm
          : shape === 'square'
            ? settings.squarePitchMm || DEFAULT_DRILL_PITCH_MM.square
            : settings.roundPitchMm || DEFAULT_DRILL_PITCH_MM.round
      );
      if (!Number.isFinite(pitch) || pitch <= 0) throw new TypeError('Drillafstand moet groter dan nul zijn.');

      var columns = Math.max(1, Math.round(size.widthMm / pitch));
      var rows = Math.max(1, Math.round(size.heightMm / pitch));

      return Object.freeze({
        widthCm: size.widthCm,
        heightCm: size.heightCm,
        orientation: size.orientation,
        aspectRatio: size.aspectRatio,
        stoneShape: shape,
        drillPitchMm: pitch,
        columns: columns,
        rows: rows,
        totalDrills: columns * rows,
        logicalAspectRatio: columns / rows,
        representedWidthCm: (columns * pitch) / 10,
        representedHeightCm: (rows * pitch) / 10,
      });
    }

    function orientCanvasSize(sizeInput, orientation) {
      var parsed = parseCanvasSize(sizeInput);
      if (!parsed) return null;
      var requested = String(orientation || '').trim().toLowerCase();
      if (requested !== 'landscape' && requested !== 'portrait') return parsed;
      var longEdge = Math.max(parsed.widthCm, parsed.heightCm);
      var shortEdge = Math.min(parsed.widthCm, parsed.heightCm);
      return parseCanvasSize(
        requested === 'landscape'
          ? { widthCm: longEdge, heightCm: shortEdge }
          : { widthCm: shortEdge, heightCm: longEdge }
      );
    }

    function calculateGrid(config) {
      var settings = config || {};
      return computePhysicalGrid(
        { widthCm: settings.widthCm, heightCm: settings.heightCm },
        settings.shape,
        {
          orientation: settings.orientation,
          pitchMm: settings.pitchMm,
          squarePitchMm: settings.squarePitch,
          roundPitchMm: settings.roundPitch,
        }
      );
    }

    function assertRgbaBuffer(source, width, height) {
      if (!source || typeof source.length !== 'number') throw new TypeError('RGBA-buffer ontbreekt.');
      if (!Number.isInteger(width) || width <= 0 || !Number.isInteger(height) || height <= 0) {
        throw new TypeError('Afbeeldingsafmetingen moeten positieve gehele getallen zijn.');
      }
      if (source.length < width * height * 4) throw new RangeError('RGBA-buffer is kleiner dan de opgegeven afmetingen.');
    }

    function sampleAreaCellLinearRgba(source, width, height, x0, y0, x1, y1) {
      assertRgbaBuffer(source, width, height);
      var left = clamp(Number(x0), 0, width);
      var top = clamp(Number(y0), 0, height);
      var right = clamp(Number(x1), 0, width);
      var bottom = clamp(Number(y1), 0, height);
      if (!(right > left) || !(bottom > top)) return [0, 0, 0, 0];

      var startX = Math.floor(left);
      var endX = Math.ceil(right);
      var startY = Math.floor(top);
      var endY = Math.ceil(bottom);
      var totalWeight = 0;
      var alphaWeight = 0;
      var red = 0;
      var green = 0;
      var blue = 0;

      for (var sourceY = startY; sourceY < endY; sourceY += 1) {
        var overlapY = Math.min(bottom, sourceY + 1) - Math.max(top, sourceY);
        if (overlapY <= 0 || sourceY < 0 || sourceY >= height) continue;
        for (var sourceX = startX; sourceX < endX; sourceX += 1) {
          var overlapX = Math.min(right, sourceX + 1) - Math.max(left, sourceX);
          if (overlapX <= 0 || sourceX < 0 || sourceX >= width) continue;
          var weight = overlapX * overlapY;
          var offset = (sourceY * width + sourceX) * 4;
          var alpha = clamp(source[offset + 3] / 255, 0, 1);
          var weightedAlpha = weight * alpha;
          totalWeight += weight;
          alphaWeight += weightedAlpha;
          red += srgbByteToLinear(source[offset]) * weightedAlpha;
          green += srgbByteToLinear(source[offset + 1]) * weightedAlpha;
          blue += srgbByteToLinear(source[offset + 2]) * weightedAlpha;
        }
      }

      if (totalWeight <= 0 || alphaWeight <= 0) return [0, 0, 0, 0];
      return [
        linearToSrgbByte(red / alphaWeight),
        linearToSrgbByte(green / alphaWeight),
        linearToSrgbByte(blue / alphaWeight),
        clampByte((alphaWeight / totalWeight) * 255),
      ];
    }

    function areaResampleRgba(source, sourceWidth, sourceHeight, targetWidth, targetHeight) {
      assertRgbaBuffer(source, sourceWidth, sourceHeight);
      if (!Number.isInteger(targetWidth) || targetWidth <= 0 || !Number.isInteger(targetHeight) || targetHeight <= 0) {
        throw new TypeError('Doelafmetingen moeten positieve gehele getallen zijn.');
      }

      var output = new Uint8ClampedArray(targetWidth * targetHeight * 4);
      var scaleX = sourceWidth / targetWidth;
      var scaleY = sourceHeight / targetHeight;

      for (var targetY = 0; targetY < targetHeight; targetY += 1) {
        for (var targetX = 0; targetX < targetWidth; targetX += 1) {
          var rgba = sampleAreaCellLinearRgba(
            source,
            sourceWidth,
            sourceHeight,
            targetX * scaleX,
            targetY * scaleY,
            (targetX + 1) * scaleX,
            (targetY + 1) * scaleY
          );
          var targetOffset = (targetY * targetWidth + targetX) * 4;
          output[targetOffset] = rgba[0];
          output[targetOffset + 1] = rgba[1];
          output[targetOffset + 2] = rgba[2];
          output[targetOffset + 3] = rgba[3];
        }
      }

      return output;
    }

    function normalizePaletteEntry(entry, index) {
      var parsed = parseHex(entry && entry.hex);
      var rgb = parsed || {
        r: clampByte(entry && entry.r),
        g: clampByte(entry && entry.g),
        b: clampByte(entry && entry.b),
      };
      return Object.freeze({
        id: String((entry && entry.id) || 'P' + String(index + 1).padStart(3, '0')),
        label: String((entry && (entry.label || entry.name)) || 'Kleur ' + (index + 1)),
        hex: rgbToHex(rgb),
        r: rgb.r,
        g: rgb.g,
        b: rgb.b,
        lab: Object.freeze(rgbToOklab(rgb)),
        sourceIndex: index,
      });
    }

    function preparePalette(palette) {
      var source = Array.isArray(palette) && palette.length ? palette : FALLBACK_PALETTE;
      return source.map(function prepareEntry(entry, index) {
        if (
          entry &&
          Number.isFinite(entry.r) &&
          Number.isFinite(entry.g) &&
          Number.isFinite(entry.b) &&
          entry.lab &&
          Number.isFinite(entry.lab.L)
        ) {
          return entry;
        }
        return normalizePaletteEntry(entry, index);
      });
    }

    var FALLBACK_PALETTE = Object.freeze(
      FALLBACK_PALETTE_SOURCE.map(function createFallback(entry, index) {
        return normalizePaletteEntry({ id: entry[0], label: entry[1], hex: entry[2] }, index);
      })
    );

    function findNearestPreparedColor(rgb, prepared) {
      if (!prepared.length) throw new RangeError('Palet bevat geen kleuren.');
      var lab = rgb && rgb.L != null ? rgb : rgbToOklab(rgb || { r: 0, g: 0, b: 0 });
      var best = prepared[0];
      var bestIndex = 0;
      var bestDistance = oklabDistanceSquared(lab, best.lab);

      for (var index = 1; index < prepared.length; index += 1) {
        var distance = oklabDistanceSquared(lab, prepared[index].lab);
        if (distance < bestDistance - 1e-15) {
          best = prepared[index];
          bestIndex = index;
          bestDistance = distance;
        }
      }

      return { color: best, index: bestIndex, distanceSquared: bestDistance };
    }

    function findNearestPaletteColor(rgb, palette) {
      return findNearestPreparedColor(rgb, preparePalette(palette));
    }

    function buildColorHistogram(rgba, options) {
      var settings = options || {};
      var alphaThreshold = settings.alphaThreshold == null ? 16 : clampByte(settings.alphaThreshold);
      var maxSamples = Math.max(1, Math.floor(settings.maxSamples || 16384));
      var labData = settings.labData;
      var hasLabData = labData && labData.length >= Math.floor(rgba.length / 4) * 3;
      var bits = clamp(Math.floor(settings.quantizationBits || 5), 3, 8);
      var shift = 8 - bits;
      var pixelCount = Math.floor((rgba && rgba.length ? rgba.length : 0) / 4);
      var sampleCount = Math.min(pixelCount, maxSamples);
      var bins = new Map();

      for (var sample = 0; sample < sampleCount; sample += 1) {
        var pixelIndex = sampleCount === pixelCount ? sample : Math.floor((sample * pixelCount) / sampleCount);
        var offset = pixelIndex * 4;
        var alpha = rgba[offset + 3];
        if (alpha < alphaThreshold) continue;
        var red = rgba[offset];
        var green = rgba[offset + 1];
        var blue = rgba[offset + 2];
        var key = (red >> shift) << (bits * 2) | (green >> shift) << bits | (blue >> shift);
        var bin = bins.get(key);
        var weight = alpha / 255;
        if (!bin) {
          bin = { key: key, weight: 0, red: 0, green: 0, blue: 0, lightness: 0, a: 0, b: 0 };
          bins.set(key, bin);
        }
        bin.weight += weight;
        bin.red += red * weight;
        bin.green += green * weight;
        bin.blue += blue * weight;
        if (hasLabData) {
          var labOffset = pixelIndex * 3;
          bin.lightness += labData[labOffset] * weight;
          bin.a += labData[labOffset + 1] * weight;
          bin.b += labData[labOffset + 2] * weight;
        }
      }

      return Array.from(bins.values())
        .sort(function sortBins(first, second) {
          return first.key - second.key;
        })
        .map(function finishBin(bin) {
          var rgb = {
            r: bin.red / bin.weight,
            g: bin.green / bin.weight,
            b: bin.blue / bin.weight,
          };
          var lab = hasLabData
            ? { L: bin.lightness / bin.weight, a: bin.a / bin.weight, b: bin.b / bin.weight }
            : rgbToOklab(rgb);
          return { key: bin.key, weight: bin.weight, rgb: rgb, lab: lab };
        });
    }

    function estimateColorComplexity(rgba, options) {
      var bins = buildColorHistogram(rgba, options);
      if (bins.length <= 1) return 0;
      var total = bins.reduce(function sumWeight(sum, bin) {
        return sum + bin.weight;
      }, 0);
      if (total <= 0) return 0;
      var entropy = bins.reduce(function sumEntropy(sum, bin) {
        var probability = bin.weight / total;
        return sum - probability * Math.log(probability);
      }, 0);
      var normalizedEntropy = entropy / Math.log(Math.min(bins.length, 256));
      var occupancy = clamp(Math.log2(bins.length + 1) / 8, 0, 1);
      return clamp(normalizedEntropy * 0.72 + occupancy * 0.28, 0, 1);
    }

    function getSizeMaxEdge(sizeInput) {
      if (sizeInput && Number.isFinite(sizeInput.widthCm) && Number.isFinite(sizeInput.heightCm)) {
        return Math.max(sizeInput.widthCm, sizeInput.heightCm);
      }
      var parsed = parseCanvasSize(sizeInput);
      return parsed ? Math.max(parsed.widthCm, parsed.heightCm) : 40;
    }

    function recommendMaxColors(sizeInput, options) {
      var settings = options || {};
      var maxEdge = getSizeMaxEdge(sizeInput);
      var policyCap = COLOR_TARGET_POLICY.find(function findPolicy(entry) {
        return maxEdge <= entry.maxEdgeCm;
      }).maxColors;
      var configuredCap = Number.isFinite(Number(settings.maxColors))
        ? Math.max(1, Math.floor(Number(settings.maxColors)))
        : policyCap;
      var hardCap = Math.min(policyCap, configuredCap);
      var minColors = clamp(Math.floor(settings.minColors || 12), 1, hardCap);
      if (!Number.isFinite(settings.complexity)) return hardCap;
      var complexity = clamp(settings.complexity, 0, 1);
      return clamp(Math.round(minColors + (hardCap - minColors) * complexity), minColors, hardCap);
    }

    function selectAdaptivePalette(rgba, palette, options) {
      var settings = options || {};
      var prepared = preparePalette(palette);
      if (!prepared.length) throw new RangeError('Palet bevat geen kleuren.');
      var bins = buildColorHistogram(rgba, settings);
      if (!bins.length) return [];
      var complexity = Number.isFinite(settings.complexity)
        ? clamp(settings.complexity, 0, 1)
        : estimateColorComplexity(rgba, settings);
      var target = Math.min(
        prepared.length,
        recommendMaxColors(settings.size || settings.grid || '40x40cm', {
          complexity: settings.adaptToComplexity === false ? undefined : complexity,
          maxColors: settings.maxColors,
          minColors: settings.minColors,
        })
      );
      var minimum = clamp(Math.floor(settings.minimumSelectedColors || 1), 1, target);
      var minRelativeGain = settings.minRelativeGain == null ? 0.00035 : Math.max(0, settings.minRelativeGain);
      var selectedIndexes = [];
      var selectedLookup = new Set();
      var bestDistances = new Float64Array(bins.length);
      bestDistances.fill(Infinity);
      var referenceError = 0;

      while (selectedIndexes.length < target) {
        var bestCandidate = -1;
        var bestGain = -Infinity;

        for (var candidate = 0; candidate < prepared.length; candidate += 1) {
          if (selectedLookup.has(candidate)) continue;
          var gain = 0;
          for (var binIndex = 0; binIndex < bins.length; binIndex += 1) {
            var candidateDistance = oklabDistanceSquared(bins[binIndex].lab, prepared[candidate].lab);
            if (selectedIndexes.length === 0) {
              gain -= candidateDistance * bins[binIndex].weight;
            } else if (candidateDistance < bestDistances[binIndex]) {
              gain += (bestDistances[binIndex] - candidateDistance) * bins[binIndex].weight;
            }
          }
          if (gain > bestGain + 1e-15 || (Math.abs(gain - bestGain) <= 1e-15 && candidate < bestCandidate)) {
            bestCandidate = candidate;
            bestGain = gain;
          }
        }

        if (bestCandidate < 0) break;
        if (
          selectedIndexes.length >= minimum &&
          referenceError > 0 &&
          bestGain >= 0 &&
          bestGain / referenceError < minRelativeGain
        ) {
          break;
        }

        selectedIndexes.push(bestCandidate);
        selectedLookup.add(bestCandidate);
        var totalError = 0;
        for (var updateIndex = 0; updateIndex < bins.length; updateIndex += 1) {
          var newDistance = oklabDistanceSquared(bins[updateIndex].lab, prepared[bestCandidate].lab);
          if (newDistance < bestDistances[updateIndex]) bestDistances[updateIndex] = newDistance;
          totalError += bestDistances[updateIndex] * bins[updateIndex].weight;
        }
        if (selectedIndexes.length === 1) referenceError = totalError;
        if (totalError <= 1e-15) break;
      }

      return selectedIndexes.map(function selectedColor(index) {
        return prepared[index];
      });
    }

    /*
     * Clean-50: build one deterministic, source-adaptive palette with at most fifty
     * base colours. A small edge-preserving bilateral pass is used only while training
     * the palette; the original resampled cells are then mapped without dithering.
     */
    function selectSourceAdaptivePalette(rgba, options) {
      var settings = options || {};
      var width = Math.floor(Number(settings.width));
      var height = Math.floor(Number(settings.height));
      var labData =
        Number.isInteger(width) && width > 0 && Number.isInteger(height) && height > 0
          ? bilateralFilterOklab(rgba, width, height, { sigmaColor: settings.sigmaColor })
          : null;
      var bins = buildColorHistogram(rgba, {
        alphaThreshold: settings.alphaThreshold,
        maxSamples: settings.maxSamples || Math.floor(rgba.length / 4),
        quantizationBits: settings.quantizationBits || 5,
        labData: labData,
      });
      if (!bins.length) return [];

      var target = Math.min(
        bins.length,
        recommendMaxColors(settings.size || settings.grid || '40x40cm', {
          maxColors: settings.maxColors,
          minColors: settings.minColors,
          complexity:
            settings.adaptToComplexity === true
              ? Number.isFinite(settings.complexity)
                ? settings.complexity
                : estimateColorComplexity(rgba, settings)
              : undefined,
        })
      );
      if (target <= 0) return [];

      var selected = [];
      var selectedLookup = new Set();
      var nearestDistances = new Float64Array(bins.length);
      nearestDistances.fill(Infinity);
      var largestWeight = 0;
      var firstIndex = 0;

      for (var binIndex = 0; binIndex < bins.length; binIndex += 1) {
        if (
          bins[binIndex].weight > largestWeight ||
          (bins[binIndex].weight === largestWeight && bins[binIndex].key < bins[firstIndex].key)
        ) {
          largestWeight = bins[binIndex].weight;
          firstIndex = binIndex;
        }
      }

      while (selected.length < target) {
        var candidateIndex = selected.length === 0 ? firstIndex : -1;
        var candidateScore = -Infinity;

        if (selected.length > 0) {
          for (var candidate = 0; candidate < bins.length; candidate += 1) {
            if (selectedLookup.has(candidate)) continue;
            /* A restrained weight keeps important small colour regions without letting
             * a single dominant background consume the complete palette. */
            var relativeWeight = largestWeight > 0 ? bins[candidate].weight / largestWeight : 1;
            var score = nearestDistances[candidate] * Math.pow(relativeWeight, 0.35);
            if (
              score > candidateScore + 1e-15 ||
              (Math.abs(score - candidateScore) <= 1e-15 &&
                (candidateIndex < 0 || bins[candidate].key < bins[candidateIndex].key))
            ) {
              candidateIndex = candidate;
              candidateScore = score;
            }
          }
        }

        if (candidateIndex < 0) break;
        selected.push({
          r: bins[candidateIndex].rgb.r,
          g: bins[candidateIndex].rgb.g,
          b: bins[candidateIndex].rgb.b,
          lab: bins[candidateIndex].lab,
        });
        selectedLookup.add(candidateIndex);

        for (var distanceIndex = 0; distanceIndex < bins.length; distanceIndex += 1) {
          var distance = oklabDistanceSquared(bins[distanceIndex].lab, bins[candidateIndex].lab);
          if (distance < nearestDistances[distanceIndex]) nearestDistances[distanceIndex] = distance;
        }
      }

      /* Deterministic Lloyd refinement in OKLab mirrors the approved Clean-50 renderer. */
      var iterations = clamp(Math.floor(settings.refinementIterations == null ? 8 : settings.refinementIterations), 0, 8);
      for (var iteration = 0; iteration < iterations && selected.length > 1; iteration += 1) {
        var weights = new Float64Array(selected.length);
        var lightness = new Float64Array(selected.length);
        var aAxis = new Float64Array(selected.length);
        var bAxis = new Float64Array(selected.length);

        for (var assignmentIndex = 0; assignmentIndex < bins.length; assignmentIndex += 1) {
          var nearestIndex = 0;
          var nearestDistance = oklabDistanceSquared(bins[assignmentIndex].lab, selected[0].lab);
          for (var centerIndex = 1; centerIndex < selected.length; centerIndex += 1) {
            var centerDistance = oklabDistanceSquared(bins[assignmentIndex].lab, selected[centerIndex].lab);
            if (centerDistance < nearestDistance - 1e-15) {
              nearestDistance = centerDistance;
              nearestIndex = centerIndex;
            }
          }
          var weight = bins[assignmentIndex].weight;
          weights[nearestIndex] += weight;
          lightness[nearestIndex] += bins[assignmentIndex].lab.L * weight;
          aAxis[nearestIndex] += bins[assignmentIndex].lab.a * weight;
          bAxis[nearestIndex] += bins[assignmentIndex].lab.b * weight;
        }

        for (var updateCenter = 0; updateCenter < selected.length; updateCenter += 1) {
          if (weights[updateCenter] <= 0) continue;
          var centroidLab = {
            L: lightness[updateCenter] / weights[updateCenter],
            a: aAxis[updateCenter] / weights[updateCenter],
            b: bAxis[updateCenter] / weights[updateCenter],
          };
          var centroid = oklabToRgb(centroidLab);
          selected[updateCenter] = {
            r: centroid.r,
            g: centroid.g,
            b: centroid.b,
            lab: centroidLab,
          };
        }
      }

      var unique = new Map();
      selected.forEach(function normalizeSourceColor(color) {
        var hex = rgbToHex(color);
        if (!unique.has(hex)) unique.set(hex, color);
      });

      return Array.from(unique.values()).map(function createSourceEntry(color, index) {
        return normalizePaletteEntry(
          {
            id: 'SRC' + String(index + 1).padStart(3, '0'),
            label: 'Clean-50 kleur ' + (index + 1),
            r: color.r,
            g: color.g,
            b: color.b,
          },
          index
        );
      });
    }

    function mapRgbaToPalette(rgba, palette, options) {
      var settings = options || {};
      var prepared = preparePalette(palette);
      if (!prepared.length) throw new RangeError('Palet bevat geen kleuren.');
      var alphaThreshold = settings.alphaThreshold == null ? 16 : clampByte(settings.alphaThreshold);
      var output = new Uint8ClampedArray(rgba.length);
      var indices = new Int16Array(Math.floor(rgba.length / 4));
      var usage = new Uint32Array(prepared.length);
      indices.fill(-1);

      for (var pixel = 0; pixel < indices.length; pixel += 1) {
        var offset = pixel * 4;
        var alpha = rgba[offset + 3];
        if (alpha < alphaThreshold) {
          output[offset + 3] = alpha;
          continue;
        }
        var nearest = findNearestPreparedColor(
          { r: rgba[offset], g: rgba[offset + 1], b: rgba[offset + 2] },
          prepared
        );
        indices[pixel] = nearest.index;
        usage[nearest.index] += 1;
        output[offset] = nearest.color.r;
        output[offset + 1] = nearest.color.g;
        output[offset + 2] = nearest.color.b;
        output[offset + 3] = alpha;
      }

      return {
        rgba: output,
        indices: indices,
        usage: usage,
        palette: prepared,
        colorCount: usage.reduce(function countUsed(count, value) {
          return count + (value > 0 ? 1 : 0);
        }, 0),
      };
    }

    function cleanupSingletons(indices, width, height, options) {
      if (!indices || indices.length < width * height) throw new RangeError('Indexbuffer is te klein.');
      var settings = options || {};
      var source = new Int16Array(indices);
      var output = new Int16Array(source);
      var palette = settings.palette ? preparePalette(settings.palette) : null;
      var minNeighbors = clamp(Math.floor(settings.minNeighbors || 5), 3, 8);
      var majorityRatio = clamp(settings.majorityRatio == null ? 0.75 : settings.majorityRatio, 0.5, 1);
      var maxDistance = settings.maxDistance == null ? 0.16 : settings.maxDistance;
      var changed = 0;

      for (var row = 0; row < height; row += 1) {
        for (var column = 0; column < width; column += 1) {
          var index = row * width + column;
          var current = source[index];
          if (current < 0) continue;
          var counts = new Map();
          var available = 0;
          var sameCount = 0;

          for (var offsetY = -1; offsetY <= 1; offsetY += 1) {
            for (var offsetX = -1; offsetX <= 1; offsetX += 1) {
              if (offsetX === 0 && offsetY === 0) continue;
              var neighborX = column + offsetX;
              var neighborY = row + offsetY;
              if (neighborX < 0 || neighborX >= width || neighborY < 0 || neighborY >= height) continue;
              var neighbor = source[neighborY * width + neighborX];
              if (neighbor < 0) continue;
              available += 1;
              if (neighbor === current) sameCount += 1;
              counts.set(neighbor, (counts.get(neighbor) || 0) + 1);
            }
          }

          if (available < minNeighbors || sameCount > 0) continue;
          var winner = -1;
          var winnerCount = 0;
          counts.forEach(function findWinner(count, value) {
            if (count > winnerCount || (count === winnerCount && value < winner)) {
              winner = value;
              winnerCount = count;
            }
          });
          if (winner < 0 || winnerCount < Math.max(minNeighbors, Math.ceil(available * majorityRatio))) continue;
          if (
            palette &&
            palette[current] &&
            palette[winner] &&
            Number.isFinite(maxDistance) &&
            Math.sqrt(oklabDistanceSquared(palette[current].lab, palette[winner].lab)) > maxDistance
          ) {
            continue;
          }
          output[index] = winner;
          changed += 1;
        }
      }

      return { indices: output, changed: changed };
    }

    function createPattern(config) {
      var settings = config || {};
      var columns = Math.floor(settings.columns);
      var rows = Math.floor(settings.rows);
      var highFidelity = settings.highFidelity === true;
      assertRgbaBuffer(settings.data, settings.width, settings.height);
      if (!Number.isInteger(columns) || columns <= 0 || !Number.isInteger(rows) || rows <= 0) {
        throw new TypeError('Patroonkolommen en -rijen moeten positieve gehele getallen zijn.');
      }

      var sampled = areaResampleRgba(settings.data, settings.width, settings.height, columns, rows);
      var size =
        settings.size ||
        (Number.isFinite(settings.widthCm) && Number.isFinite(settings.heightCm)
          ? { widthCm: settings.widthCm, heightCm: settings.heightCm }
          : '40x40cm');
      var selectedPalette = highFidelity
        ? selectSourceAdaptivePalette(sampled, {
            width: columns,
            height: rows,
            size: size,
            maxColors: settings.maxColors,
            minColors: settings.minColors,
            alphaThreshold: settings.alphaThreshold,
            maxSamples: settings.maxSamples,
            quantizationBits: settings.quantizationBits,
            refinementIterations: settings.refinementIterations == null ? 8 : settings.refinementIterations,
            adaptToComplexity: settings.adaptToComplexity,
          })
        : selectAdaptivePalette(sampled, settings.palette || FALLBACK_PALETTE, {
            size: size,
            maxColors: settings.maxColors,
            minColors: settings.minColors,
            minimumSelectedColors: settings.minimumSelectedColors,
            minRelativeGain: settings.minRelativeGain,
            alphaThreshold: settings.alphaThreshold,
            maxSamples: settings.maxSamples,
          });
      if (!selectedPalette.length) {
        var transparentIndices = new Int16Array(columns * rows);
        transparentIndices.fill(-1);
        return {
          indices: transparentIndices,
          palette: [],
          colorsUsed: 0,
          columns: columns,
          rows: rows,
          sampledRgba: sampled,
          cellRgba: sampled,
          paletteMode: highFidelity ? 'clean-50' : 'fixed',
          cleanupChanged: 0,
        };
      }
      var mapped = mapRgbaToPalette(sampled, selectedPalette, {
        alphaThreshold: settings.alphaThreshold,
      });
      var finalIndices = mapped.indices;
      var cleanupChanged = 0;

      if (settings.cleanup) {
        var cleanupSettings = typeof settings.cleanup === 'object' ? settings.cleanup : {};
        var cleaned = cleanupSingletons(finalIndices, columns, rows, {
          palette: selectedPalette,
          minNeighbors: cleanupSettings.minNeighbors,
          majorityRatio: cleanupSettings.majorityRatio,
          maxDistance: cleanupSettings.maxDistance,
        });
        finalIndices = cleaned.indices;
        cleanupChanged = cleaned.changed;
      }

      var used = new Set();
      for (var pixel = 0; pixel < finalIndices.length; pixel += 1) {
        if (finalIndices[pixel] >= 0) used.add(finalIndices[pixel]);
      }

      return {
        indices: finalIndices,
        palette: selectedPalette,
        colorsUsed: used.size,
        columns: columns,
        rows: rows,
        sampledRgba: sampled,
        cellRgba: mapped.rgba,
        paletteMode: highFidelity ? 'clean-50' : 'fixed',
        cleanupChanged: cleanupChanged,
      };
    }

    function getStoneRenderMetadata(stoneShape) {
      var shape = normalizeStoneShape(stoneShape);
      if (shape === 'round') {
        var diameterRatio = 0.94;
        return Object.freeze({
          shape: 'round',
          geometry: 'circle',
          extentRatio: diameterRatio,
          diameterRatio: diameterRatio,
          gapRatio: 1 - diameterRatio,
          areaCoverageRatio: Math.PI * Math.pow(diameterRatio / 2, 2),
          cornerRadiusRatio: 0.5,
          facetInsetRatio: 0.22,
          highlightAlpha: 0.24,
          shadowAlpha: 0.18,
          canvasVisibleAtCorners: true,
        });
      }

      var sideRatio = 0.96;
      return Object.freeze({
        shape: 'square',
        geometry: 'rounded-square',
        extentRatio: sideRatio,
        sideRatio: sideRatio,
        gapRatio: 1 - sideRatio,
        areaCoverageRatio: sideRatio * sideRatio,
        cornerRadiusRatio: 0.07,
        facetInsetRatio: 0.18,
        highlightAlpha: 0.22,
        shadowAlpha: 0.16,
        canvasVisibleAtCorners: false,
      });
    }

    function stableCellHash(column, row, seed) {
      var value = (Math.imul(column + 1, 374761393) + Math.imul(row + 1, 668265263) + (seed | 0)) | 0;
      value = Math.imul(value ^ (value >>> 13), 1274126177);
      return ((value ^ (value >>> 16)) >>> 0) / 4294967295;
    }

    function getStoneCellMetadata(stoneShape, column, row, cellWidth, cellHeight, options) {
      var base = getStoneRenderMetadata(stoneShape);
      var settings = options || {};
      var width = Math.max(0, Number(cellWidth) || 0);
      var height = Math.max(0, Number(cellHeight) || 0);
      var extent = Math.min(width, height) * base.extentRatio;
      var hash = stableCellHash(Math.floor(column), Math.floor(row), Math.floor(settings.seed || 0));
      return Object.freeze({
        shape: base.shape,
        geometry: base.geometry,
        x: (Number(column) || 0) * width + (width - extent) / 2,
        y: (Number(row) || 0) * height + (height - extent) / 2,
        width: extent,
        height: extent,
        radius: base.shape === 'round' ? extent / 2 : extent * base.cornerRadiusRatio,
        facetInset: extent * base.facetInsetRatio,
        highlightAlpha: base.highlightAlpha,
        shadowAlpha: base.shadowAlpha,
        facetAngleDegrees: Math.floor(hash * 4) * 90 + 45,
        glintStrength: 0.82 + hash * 0.18,
      });
    }

    function shadeRgb(rgb, factor) {
      return {
        r: linearToSrgbByte(srgbByteToLinear(rgb.r) * factor),
        g: linearToSrgbByte(srgbByteToLinear(rgb.g) * factor),
        b: linearToSrgbByte(srgbByteToLinear(rgb.b) * factor),
      };
    }

    function shadeRgbLuminanceOnly(rgb, factor) {
      var red = srgbByteToLinear(rgb.r);
      var green = srgbByteToLinear(rgb.g);
      var blue = srgbByteToLinear(rgb.b);
      var safeFactor = Math.max(0, Number(factor) || 0);
      var brightest = Math.max(red, green, blue);
      if (safeFactor > 1 && brightest > 0) safeFactor = Math.min(safeFactor, 1 / brightest);
      return {
        r: linearToSrgbByte(red * safeFactor),
        g: linearToSrgbByte(green * safeFactor),
        b: linearToSrgbByte(blue * safeFactor),
      };
    }

    function renderPattern(config) {
      var settings = config || {};
      var columns = Math.floor(settings.columns);
      var rows = Math.floor(settings.rows);
      if (!Number.isInteger(columns) || columns <= 0 || !Number.isInteger(rows) || rows <= 0) {
        throw new TypeError('Patroonkolommen en -rijen moeten positieve gehele getallen zijn.');
      }
      if (!settings.indices || settings.indices.length < columns * rows) {
        throw new RangeError('Patroonindexbuffer is te klein.');
      }
      var cellRgba = settings.cellRgba || settings.sampledRgba || null;
      var usesCellColors = Boolean(cellRgba && cellRgba.length >= columns * rows * 4);
      var palette = usesCellColors ? [] : preparePalette(settings.palette);
      var shape = normalizeStoneShape(settings.shape);
      var renderMeta = getStoneRenderMetadata(shape);
      var maxOutputPixels = Math.max(1, Math.floor(settings.maxOutputPixels || 4000000));
      var automaticCellSize = Math.floor(Math.sqrt(maxOutputPixels / (columns * rows)));
      var cellSize = clamp(Math.floor(settings.cellSize || automaticCellSize || 1), 1, 12);
      var width = columns * cellSize;
      var height = rows * cellSize;
      var background =
        parseHex(settings.background) ||
        (settings.background && Number.isFinite(settings.background.r)
          ? {
              r: clampByte(settings.background.r),
              g: clampByte(settings.background.g),
              b: clampByte(settings.background.b),
            }
          : { r: 245, g: 245, b: 245 });
      var output = new Uint8ClampedArray(width * height * 4);

      for (var outputPixel = 0; outputPixel < width * height; outputPixel += 1) {
        var backgroundOffset = outputPixel * 4;
        output[backgroundOffset] = background.r;
        output[backgroundOffset + 1] = background.g;
        output[backgroundOffset + 2] = background.b;
        output[backgroundOffset + 3] = 255;
      }

      for (var row = 0; row < rows; row += 1) {
        for (var column = 0; column < columns; column += 1) {
          var cellIndex = row * columns + column;
          var paletteIndex = settings.indices[cellIndex];
          var color;
          if (usesCellColors) {
            var colorOffset = cellIndex * 4;
            if (cellRgba[colorOffset + 3] < 16) continue;
            color = {
              r: cellRgba[colorOffset],
              g: cellRgba[colorOffset + 1],
              b: cellRgba[colorOffset + 2],
            };
          } else {
            if (paletteIndex < 0 || !palette[paletteIndex]) continue;
            color = palette[paletteIndex];
          }
          var cellMeta = getStoneCellMetadata(shape, column, row, cellSize, cellSize, {
            seed: settings.seed,
          });
          var centerX = column * cellSize + cellSize / 2;
          var centerY = row * cellSize + cellSize / 2;
          var halfExtent = (cellSize * renderMeta.extentRatio) / 2;
          var minimumX = Math.floor(column * cellSize);
          var maximumX = Math.ceil((column + 1) * cellSize);
          var minimumY = Math.floor(row * cellSize);
          var maximumY = Math.ceil((row + 1) * cellSize);

          for (var y = minimumY; y < maximumY; y += 1) {
            for (var x = minimumX; x < maximumX; x += 1) {
              var normalizedX = (x + 0.5 - centerX) / halfExtent;
              var normalizedY = (y + 0.5 - centerY) / halfExtent;
              var inside =
                shape === 'round'
                  ? normalizedX * normalizedX + normalizedY * normalizedY <= 1
                  : Math.max(Math.abs(normalizedX), Math.abs(normalizedY)) <= 1;
              if (!inside) continue;

              var direction = normalizedX * 0.58 - normalizedY * 0.72;
              var facet = Math.abs(normalizedX) > Math.abs(normalizedY) ? normalizedX : -normalizedY;
              var factor;
              var shaded;
              if (usesCellColors) {
                /* Keep the source hue/chroma intact. A restrained, linear-light
                 * luminance modulation supplies the drill facet without posterising
                 * faces or introducing pink/purple colour branches. */
                factor =
                  1 +
                  direction * 0.036 * cellMeta.glintStrength +
                  (facet >= 0 ? 0.014 : -0.012);
                if (shape === 'round') {
                  var highFidelityRadius = Math.sqrt(normalizedX * normalizedX + normalizedY * normalizedY);
                  factor += (1 - highFidelityRadius) * 0.018;
                }
                shaded = shadeRgbLuminanceOnly(color, clamp(factor, 0.92, 1.08));
              } else {
                factor =
                  1 +
                  direction * 0.1 * cellMeta.glintStrength +
                  (facet >= 0 ? cellMeta.highlightAlpha : -cellMeta.shadowAlpha) * 0.22;
                if (shape === 'round') {
                  var radius = Math.sqrt(normalizedX * normalizedX + normalizedY * normalizedY);
                  factor += (1 - radius) * 0.08;
                }
                shaded = shadeRgb(color, clamp(factor, 0.68, 1.28));
              }
              var offset = (y * width + x) * 4;
              output[offset] = shaded.r;
              output[offset + 1] = shaded.g;
              output[offset + 2] = shaded.b;
              output[offset + 3] = 255;
            }
          }
        }
      }

      return {
        data: output,
        width: width,
        height: height,
        cellSize: cellSize,
        shape: shape,
        coverage: renderMeta.areaCoverageRatio,
      };
    }

    return Object.freeze({
      VERSION: VERSION,
      DEFAULT_DRILL_PITCH_MM: DEFAULT_DRILL_PITCH_MM,
      COLOR_TARGET_POLICY: COLOR_TARGET_POLICY,
      FALLBACK_PALETTE: FALLBACK_PALETTE,
      normalizeStoneShape: normalizeStoneShape,
      parseCanvasSize: parseCanvasSize,
      orientCanvasSize: orientCanvasSize,
      computePhysicalGrid: computePhysicalGrid,
      calculateGrid: calculateGrid,
      srgbByteToLinear: srgbByteToLinear,
      linearToSrgbByte: linearToSrgbByte,
      sampleAreaCellLinearRgba: sampleAreaCellLinearRgba,
      areaResampleRgba: areaResampleRgba,
      rgbToOklab: rgbToOklab,
      oklabToRgb: oklabToRgb,
      oklabDistanceSquared: oklabDistanceSquared,
      bilateralFilterOklab: bilateralFilterOklab,
      preparePalette: preparePalette,
      findNearestPaletteColor: findNearestPaletteColor,
      estimateColorComplexity: estimateColorComplexity,
      recommendMaxColors: recommendMaxColors,
      selectAdaptivePalette: selectAdaptivePalette,
      selectSourceAdaptivePalette: selectSourceAdaptivePalette,
      mapRgbaToPalette: mapRgbaToPalette,
      cleanupSingletons: cleanupSingletons,
      createPattern: createPattern,
      getStoneRenderMetadata: getStoneRenderMetadata,
      getStoneCellMetadata: getStoneCellMetadata,
      renderPattern: renderPattern,
    });
  }
);
